/**
 * DebtLine — REDESIGN §6's debt trend: total debt over 12 months, one line.
 *
 * Same drawing conventions as the other ported charts (spec-business §6): the plot is an LTR
 * coordinate system inside the RTL page, 1 unit = 1 CSS px, `niceTicks` produces the "0 / 40K"
 * axis, every point is a focusable hit area sharing one tooltip, and `ChartBlock` supplies the
 * table twin and the verbal summary.
 *
 * The one thing this chart adds: a segment whose points are `estimated` is drawn **dashed**, so a
 * schedule projection is never mistaken for measured history. That is the only difference between
 * the server's 12-month `balance_snapshots` line and the client's forward projection.
 */

import type { DebtPoint } from '../loans.ts';
import { ChartBlock, type TableTwin } from './ChartBlock.tsx';
import { ChartTip, useTooltip, type TipContent } from './Tooltip.tsx';
import { areaPath, linePath, niceTicks } from './chart-utils.ts';
import { useChartWidth } from './useChartWidth.ts';
import { fmtK, fmtMoney, monthShort } from '../format.ts';

export function DebtLine({
  points,
  height = 160,
  labelEvery = 3,
  summary,
  table,
  tip,
}: {
  points: DebtPoint[];
  height?: number;
  labelEvery?: number;
  summary: string;
  table: TableTwin;
  tip: (index: number) => TipContent;
}) {
  const [ref, width] = useChartWidth(480);
  const tooltip = useTooltip();
  if (points.length < 2) return <div ref={ref} />;

  const W = Math.max(width, 200);
  const mL = 44;
  const mR = 14;
  const mT = 14;
  const mB = 22;
  const pw = W - mL - mR;
  const ph = height - mT - mB;
  const values = points.map((point) => point.debtAgorot);
  const ticks = niceTicks(0, Math.max(...values) * 1.12, 4);
  const x = (i: number) => mL + (i / (points.length - 1)) * pw;
  const y = (v: number) => mT + ph - (v / (ticks.max || 1)) * ph;
  const coords = values.map((v, i) => [x(i), y(v)] as const);
  const slot = pw / (points.length - 1);
  const lastIndex = points.length - 1;

  // Split the line where the series stops being measured, so history stays solid and the
  // projection is dashed. `firstEstimated - 1` keeps the two halves joined.
  const firstEstimated = points.findIndex((point) => point.estimated);
  const solid = firstEstimated < 0 ? coords : coords.slice(0, Math.max(1, firstEstimated));
  const dashed = firstEstimated < 0 ? [] : coords.slice(Math.max(0, firstEstimated - 1));

  return (
    <div ref={ref}>
      <ChartBlock summary={summary} table={table}>
        <div className="plot">
          <svg viewBox={`0 0 ${W} ${height}`} width={W} height={height}>
            <g className="grid">
              {ticks.ticks.map((t) => (
                <line key={t} x1={mL} x2={W - mR} y1={y(t)} y2={y(t)} />
              ))}
            </g>
            <g className="axis">
              {ticks.ticks.map((t) => (
                <text key={t} x={mL - 6} y={y(t) + 3.5} textAnchor="end" className="lbl2">
                  {fmtK(t)}
                </text>
              ))}
            </g>
            <path d={areaPath(coords, mT + ph)} fill="var(--series-2)" opacity=".10" />
            {solid.length > 1 ? <path d={linePath(solid)} fill="none" stroke="var(--series-2)" strokeWidth="2" strokeLinejoin="round" /> : null}
            {dashed.length > 1 ? (
              <path d={linePath(dashed)} fill="none" stroke="var(--series-2)" strokeWidth="2" strokeLinejoin="round" className="fc" />
            ) : null}
            {points.map((point, i) =>
              i % labelEvery === 0 || i === lastIndex ? (
                <text key={point.month} x={x(i)} y={height - 7} textAnchor="middle" className="lbl3">
                  {monthShort(point.month, false)}
                </text>
              ) : null,
            )}
            <circle cx={x(0)} cy={y(values[0]!)} r="4.5" fill="var(--series-2)" className="ring" />
            <text x={x(0) + 8} y={y(values[0]!) - 9} textAnchor="start" className="lbl">
              {fmtMoney(values[0]!)}
            </text>
            {points.map((point, i) => {
              const content = tip(i);
              const handlers = tooltip.bind(content, { x: x(i), y: mT });
              return (
                <rect
                  key={`hit-${point.month}`}
                  className="bar-hit"
                  x={x(i) - slot / 2}
                  y={mT}
                  width={slot}
                  height={ph}
                  tabIndex={0}
                  role="img"
                  aria-label={`${content.title}: ${content.rows.map((r) => `${r.label} ${r.value}`).join(', ')}`}
                  {...handlers}
                />
              );
            })}
          </svg>
        </div>
        <ChartTip state={tooltip.state} width={W} />
      </ChartBlock>
    </div>
  );
}
